import { useMemo } from "react";
import { Divider } from "@mui/material";
import BaseTable from "@components/DataTable/BaseTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { operationTypes, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Cargo, CustomerTerminal, Parcel } from "@services/vessel-nomination/types";
import { useTranslation } from "react-i18next";
import { LANG_BASE } from "../../VesselNominationDetails";

const PARCEL_TABLE_FIELDS = Object.freeze({
  PARCEL_SEQUENCE: "parcelSequence",
  TERMINAL_NAME: "terminalName",
  OPERATION_TYPE: "operationType",
  CARGO_TYPE: "cargoType",
  CARGO_QUANTITY_CBM: "cargoQuantityCbm",
  CARGO_QUANTITY_MT: "cargoQuantityMt",
});

type ParcelsTableRow = {
  id: number;
  [PARCEL_TABLE_FIELDS.PARCEL_SEQUENCE]: number;
  [PARCEL_TABLE_FIELDS.TERMINAL_NAME]: string;
  [PARCEL_TABLE_FIELDS.OPERATION_TYPE]: string;
  [PARCEL_TABLE_FIELDS.CARGO_TYPE]: string;
  [PARCEL_TABLE_FIELDS.CARGO_QUANTITY_CBM]?: number | string;
  [PARCEL_TABLE_FIELDS.CARGO_QUANTITY_MT]?: number | string;
};

type ReferenceOption = {
  label: string;
  value: string;
};

interface ParcelsTableProps {
  rows?: Parcel[];
}

const getReferenceLabel = (options: ReferenceOption[], value?: string) => {
  if (!value) return "-";
  return options.find((option) => option.value === value)?.label ?? value;
};

const getCargoTypeLabel = (cargoTypes: Cargo[], cargoType?: Cargo) => {
  if (!cargoType) return "-";
  return (
    cargoTypes.find((cargo) => cargo.cargoCode === cargoType.cargoCode)?.cargoDescription ??
    cargoType.cargoCode
  );
};

const getTerminalLabel = (terminals: CustomerTerminal[], terminal?: CustomerTerminal) => {
  if (!terminal) return "-";
  return (
    terminals.find((t) => t.companyCode === terminal.companyCode)?.companyNameEnglish ??
    terminal.companyCode
  );
};

const ParcelsTable = ({ rows }: ParcelsTableProps) => {
  const { t } = useTranslation();

  const { options: cargoTypes } = useFormattedReference<Cargo>(
    `${REFERENCE_DATA_TYPES.CARGO}?cargoTypeCodes=LB,FO,CO`,
    "bff-vessel",
  );

  const { options: terminalOptions } = useFormattedReference<CustomerTerminal>(
    REFERENCE_DATA_TYPES.CUSTOMER_TERMINAL,
    "bff-vessel",
  );

  const headers: Header[] = useMemo(
    () => [
      {
        field: PARCEL_TABLE_FIELDS.PARCEL_SEQUENCE,
        columnName: t(`${LANG_BASE}.paq.cargo-table-headers.parcel-sequence`),
      },
      {
        field: PARCEL_TABLE_FIELDS.TERMINAL_NAME,
        columnName: t(`${LANG_BASE}.paq.cargo-table-headers.terminal-name`),
      },
      {
        field: PARCEL_TABLE_FIELDS.OPERATION_TYPE,
        columnName: t(`${LANG_BASE}.paq.cargo-table-headers.operation-type`),
      },
      {
        field: PARCEL_TABLE_FIELDS.CARGO_TYPE,
        columnName: t(`${LANG_BASE}.paq.cargo-table-headers.cargo-type`),
      },
      {
        field: PARCEL_TABLE_FIELDS.CARGO_QUANTITY_CBM,
        columnName: t(`${LANG_BASE}.paq.cargo-table-headers.cargo-quantity-cbm`),
      },
      {
        field: PARCEL_TABLE_FIELDS.CARGO_QUANTITY_MT,
        columnName: t(`${LANG_BASE}.paq.cargo-table-headers.cargo-quantity-mt`),
      },
    ],
    [t],
  );

  const mappedRows: ParcelsTableRow[] = (rows ?? []).map((parcel, index) => {
    return {
      id: index,
      [PARCEL_TABLE_FIELDS.PARCEL_SEQUENCE]: index + 1,
      [PARCEL_TABLE_FIELDS.TERMINAL_NAME]: getTerminalLabel(
        terminalOptions,
        parcel.customerTerminal,
      ),
      [PARCEL_TABLE_FIELDS.OPERATION_TYPE]: getReferenceLabel(operationTypes, parcel.operationType),
      [PARCEL_TABLE_FIELDS.CARGO_TYPE]: getCargoTypeLabel(cargoTypes, parcel.cargoType),
      [PARCEL_TABLE_FIELDS.CARGO_QUANTITY_CBM]: parcel.cargoQuantityCbm ?? "-",
      [PARCEL_TABLE_FIELDS.CARGO_QUANTITY_MT]: parcel.cargoQuantityMt ?? "-",
    };
  });

  return (
    <>
      <BaseTable rows={mappedRows} headers={headers} />
      <Divider className="w-full" />
    </>
  );
};

export default ParcelsTable;
